<template>
  <div class="section">
    <div class="toolbar">
      <div class="toolbar-filters">
        <label class="field-inline">
          <span>ID контакта</span>
          <input v-model.number="filters.contact_id" type="number" class="filter-input" placeholder="все" @keyup.enter="applyFilters">
        </label>
        <label class="field-inline">
          <span>Статус</span>
          <select v-model="filters.state" class="select" @change="applyFilters">
            <option value="">все</option>
            <option v-for="opt in STATE_OPTIONS" :key="opt" :value="opt">{{ stateLabel(opt) }}</option>
          </select>
        </label>
        <label class="field-inline">
          <span>Источник</span>
          <select v-model="filters.publication_type" class="select" @change="applyFilters">
            <option value="">все</option>
            <option value="auto">Авто</option>
            <option value="manual">Ручной</option>
          </select>
        </label>
      </div>
      <div class="toolbar-meta">
        <span class="hint">Автообновление каждые 30с</span>
        <button type="button" class="btn-secondary" :disabled="loading" @click="loadAssignments">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
      </div>
    </div>

    <div v-if="hasError" class="error-message" role="alert">Не удалось загрузить выложенные грузы.</div>

    <div class="table-wrap">
      <div v-if="loading && items.length > 0" class="refresh-bar" aria-hidden="true" />
      <my-loader v-if="loading && items.length === 0" class="table-loader" />
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Направление</th>
            <th>Почта</th>
            <th>Рейсов</th>
            <th class="col-money">Цена min–max (тыс.)</th>
            <th>Дата погрузки</th>
            <th>Заявки</th>
            <th>Источник</th>
            <th>Статус</th>
            <th class="col-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredItems.length === 0">
            <td :colspan="9" class="empty-cell">Грузов нет</td>
          </tr>
          <tr v-for="item in filteredItems" :key="item.cargo_application_id || (item.direction_key + ':' + item.contact_id)">
            <td data-label="Направление" class="mono">{{ directionLabel(item) }}</td>
            <td data-label="Почта">{{ item.contact_email || '—' }}</td>
            <td data-label="Рейсов">{{ item.trip_count }}</td>
            <td data-label="Цена min–max (тыс.)" class="col-money">{{ formatRange(item.price_min, item.price_max) }}</td>
            <td data-label="Дата погрузки">{{ formatDate(item.loading_date) }}</td>
            <td data-label="Заявки">
              <div v-if="item.applications.length" class="application-list">
                <template v-for="application in item.applications" :key="applicationKey(application)">
                  <router-link
                    v-if="hasSourceId(application)"
                    class="application-link mono"
                    :to="{ name: 'Distribution', query: { tripId: application.source_id } }"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ application.uid }}</router-link>
                  <span v-else class="mono">{{ application.uid }}</span>
                </template>
              </div>
              <span v-else>—</span>
            </td>
            <td data-label="Источник">
              <span class="badge" :class="item.publication_type === 'manual' ? 'badge-manual' : 'badge-auto'">
                {{ item.publication_type === 'manual' ? 'Ручной' : 'Авто' }}
              </span>
            </td>
            <td data-label="Статус">
              <span
                class="badge"
                :class="stateClass(item.state)"
                :title="item.suppression_reason || item.suppress_reason || ''"
              >{{ stateLabel(item.state) }}</span>
            </td>
            <td data-label="Действия" class="col-actions">
              <div class="action-buttons">
                <a
                  v-if="item.cargo_application_id"
                  class="btn-ati"
                  :href="atiCargoUrl(item.cargo_application_id)"
                  target="_blank"
                  rel="noopener noreferrer"
                >Открыть в ATI ↗</a>
                <span
                  v-else
                  class="btn-ati btn-ati-disabled"
                  aria-disabled="true"
                  title="Идентификатор груза ATI недоступен"
                >Открыть в ATI ↗</span>
                <button type="button" class="btn-edit" @click="toBinding(item)">→ в привязку</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { getOverview } from '@/backend/distributorRoutingApi'

const STATE_OPTIONS = ['published', 'failed', 'retry_wait', 'unknown']

const STATE_LABELS = {
  planned: 'Запланирован',
  published: 'Опубликован',
  suppressed: 'Не опубликован',
  no_victim: 'Не опубликован',
  evicted: 'Вытеснен',
  failed: 'Ошибка',
  retry_wait: 'Ожидает повтора',
  unknown: 'Неизвестно',
}

const stateLabel = (state) => (state ? (STATE_LABELS[state] || state) : '—')

export default {
  name: 'AssignmentsSection',
  props: {
    active: { type: Boolean, default: false },
  },
  emits: ['alerts', 'create-binding'],
  setup(props, { emit }) {
    const items = ref([])
    const loading = ref(false)
    const hasError = ref(false)
    const filters = reactive({ contact_id: null, state: '', publication_type: '' })
    const filteredItems = computed(() => items.value.filter((item) => (
      (!filters.state || item.state === filters.state)
      && (!filters.publication_type || item.publication_type === filters.publication_type)
    )))
    let pollTimer = null
    let requestInFlight = false
    let refreshQueued = false
    let disposed = false
    let filtersVersion = 0

    const clearPoll = () => {
      if (pollTimer) clearTimeout(pollTimer)
      pollTimer = null
    }

    const schedulePoll = () => {
      clearPoll()
      if (!disposed && props.active) pollTimer = setTimeout(loadAssignments, 30000)
    }

    const loadAssignments = async () => {
      if (requestInFlight) {
        refreshQueued = true
        return
      }
      requestInFlight = true
      loading.value = true
      clearPoll()
      const requestFiltersVersion = filtersVersion
      const params = { limit: 500 }
      if (filters.contact_id) params.contact_id = filters.contact_id
      try {
        const data = await getOverview(params)
        if (!disposed && requestFiltersVersion === filtersVersion) {
          const overviewItems = Array.isArray(data?.items) ? data.items : []
          items.value = overviewItems
            .filter((item) => (
              item.actual_presence === 'present'
              && item.desired?.state !== 'suppressed'
            ))
            .map((item) => ({
              ...item,
              ...(item.desired || {}),
              applications: Array.isArray(item.applications) ? item.applications : [],
              cargo_application_id: item.desired?.cargo_application_id
                || item.cargo_application_id
                || item.actual?.cargo_application_id,
            }))
          hasError.value = false
          emit('alerts', 0)
        }
      } catch {
        if (!disposed && requestFiltersVersion === filtersVersion) hasError.value = true
      } finally {
        requestInFlight = false
        if (!disposed) loading.value = false
        if (refreshQueued && !disposed) {
          refreshQueued = false
          loadAssignments()
        } else {
          schedulePoll()
        }
      }
    }

    const applyFilters = () => {
      filtersVersion += 1
      loadAssignments()
    }

    const formatRange = (min, max) => {
      if (min == null && max == null) return '—'
      if (min != null && max != null && min !== max) return `${min}–${max}`
      return `${min != null ? min : max}`
    }

    const directionLabel = (item) => {
      if (item.from_city_name && item.to_city_name) {
        const from = item.from_region_name
          ? `${item.from_city_name}, ${item.from_region_name}`
          : item.from_city_name
        const to = item.to_region_name
          ? `${item.to_city_name}, ${item.to_region_name}`
          : item.to_city_name
        return `${from} → ${to}`
      }
      return `ID ${item.from_city_id} → ID ${item.to_city_id}`
    }

    const formatDate = (value) => {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('ru-RU')
    }

    const atiCargoUrl = (cargoApplicationId) => (
      `https://loads.ati.su/loadinfo/${encodeURIComponent(cargoApplicationId)}`
    )

    const stateClass = (state) => {
      switch (state) {
        case 'published': return 'badge-published'
        case 'planned': return 'badge-planned'
        case 'suppressed': return 'badge-suppressed'
        case 'no_victim': return 'badge-suppressed'
        case 'evicted': return 'badge-evicted'
        case 'failed': return 'badge-failed'
        case 'retry_wait': return 'badge-retry-wait'
        case 'unknown': return 'badge-unknown'
        default: return 'badge-unknown'
      }
    }

    const toBinding = (item) => {
      emit('create-binding', {
        from_city_id: item.from_city_id,
        to_city_id: item.to_city_id,
        contact_id: item.contact_id,
      })
    }

    const hasSourceId = (application) => (
      application.source_id !== null
      && application.source_id !== undefined
      && application.source_id !== ''
    )
    const applicationKey = (application) => `${application.uid}:${application.source_id || ''}`

    watch(() => props.active, (active) => {
      clearPoll()
      if (active) loadAssignments()
    })
    onMounted(() => {
      emit('alerts', 0)
      loadAssignments()
    })
    onBeforeUnmount(() => {
      disposed = true
      clearPoll()
    })

    return {
      STATE_OPTIONS, items, filteredItems, loading, hasError, filters,
      stateLabel,
      loadAssignments, applyFilters, formatRange, formatDate, atiCargoUrl,
      stateClass, toBinding, directionLabel, hasSourceId, applicationKey,
    }
  },
}
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.toolbar-filters { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.toolbar-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; margin-left: auto; }
.field-inline { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: #475569; }
.filter-input, .select { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; font-size: 13px; height: 36px; padding: 8px 10px; }
.filter-input { width: 110px; }
.select { width: 150px; }
.hint { color: #98a2b3; font-size: 11px; }
.table-wrap { min-height: 180px; overflow-x: auto; position: relative; }
:deep(.table-loader.loading) { height: 180px; }
.data-table { border-collapse: collapse; width: 100%; font-size: 13px; min-width: 980px; }
.data-table th, .data-table td { border-bottom: 1px solid #edf0f3; padding: 8px 10px; text-align: left; vertical-align: middle; }
.data-table th { background: #f8fafc; color: #475569; font-size: 11px; font-weight: 800; white-space: nowrap; }
.data-table tbody tr:hover { background: #f3f6fb; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.application-list { align-items: center; display: flex; flex-wrap: wrap; gap: 4px 8px; max-width: 240px; }
.application-link { color: #2563eb; overflow-wrap: anywhere; text-decoration: none; }
.application-link:hover { text-decoration: underline; }
.col-money { text-align: right; white-space: nowrap; }
.empty-cell { text-align: center !important; color: #6b7280; padding: 24px !important; }
.badge { align-items: center; box-sizing: border-box; display: inline-flex; font-size: 11px; font-weight: 700; height: 30px; padding: 6px 10px; border-radius: 8px; vertical-align: middle; }
.badge-published { background: #e6f4ea; color: #1e7e34; }
.badge-planned { background: #fff5e0; color: #b7791f; }
.badge-suppressed { background: #f1f5f9; color: #6b7280; }
.badge-evicted { background: #fdeaea; color: #c0392b; }
.badge-failed { background: #fee2e2; color: #991b1b; }
.badge-retry-wait { background: #fff7ed; color: #c2410c; }
.badge-unknown { background: #e8eefc; color: #2563eb; }
.badge-auto { background: #e8eefc; color: #2563eb; }
.badge-manual { background: #f3e8ff; color: #7e22ce; }
.error-message { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
button { border: none; border-radius: 8px; box-sizing: border-box; cursor: pointer; font-weight: 700; font-size: 12px; height: 36px; padding: 0 14px; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.toolbar-meta .btn-secondary { min-width: 150px; }
.data-table th.col-actions, .data-table td.col-actions { text-align: right; white-space: nowrap; }
.action-buttons { align-items: center; display: flex; gap: 6px; justify-content: flex-end; }
.action-buttons .btn-edit { align-items: center; display: inline-flex; height: 30px; line-height: 1; padding: 0 10px; }
.btn-ati { align-items: center; background: #fff7ed; border: none; border-radius: 8px; box-sizing: border-box; color: #c2410c; display: inline-flex; font-size: 12px; font-weight: 700; height: 30px; line-height: 1; padding: 0 10px; text-decoration: none; transition: background-color 0.15s ease, color 0.15s ease; }
.btn-ati:hover { background: #ffedd5; color: #9a3412; }
.btn-ati:active { background: #fed7aa; }
.btn-ati-disabled, .btn-ati-disabled:hover, .btn-ati-disabled:active { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
.btn-edit { background: #eef4ff; color: #1d4ed8; }
.refresh-bar { height: 2px; left: 0; overflow: hidden; position: absolute; right: 0; top: 0; z-index: 2; }
.refresh-bar::after { animation: refresh 1.2s ease-in-out infinite; background: #2563eb; content: ''; height: 100%; left: -35%; position: absolute; width: 35%; }
@keyframes refresh { to { left: 100%; } }

@media (max-width: 760px) {
  .toolbar, .toolbar-filters { align-items: stretch; flex-direction: column; }
  .toolbar-meta { align-items: stretch; margin-left: 0; }
  .filter-input, .select { width: 100%; }
  .table-wrap { overflow: visible; }
  .data-table { min-width: 0; }
  .data-table thead { display: none; }
  .data-table tbody, .data-table tr, .data-table td { display: block; width: 100%; }
  .data-table tr { border: 1px solid #e5e7eb; border-radius: 10px; box-sizing: border-box; margin-bottom: 12px; overflow: hidden; }
  .data-table td { align-items: flex-start; border-bottom: 1px solid #edf0f3; box-sizing: border-box; display: grid; gap: 10px; grid-template-columns: 105px minmax(0, 1fr); padding: 9px 10px; text-align: left !important; white-space: normal; }
  .data-table td::before { color: #64748b; content: attr(data-label); font-size: 10px; font-weight: 800; text-transform: uppercase; }
  .data-table td:last-child { border-bottom: none; }
  .data-table .empty-cell { display: block; }
  .data-table .empty-cell::before { content: none; }
  .application-list { max-width: none; }
  .action-buttons { flex-wrap: wrap; justify-content: flex-start; }
}
</style>
